/**
 * Returns a debounced function that delays invoking `fn` until after `waitMs`
 * milliseconds have elapsed since the last call. Includes `.cancel()` like
 * lodash debounce.
 *
 * @param fn - Function to debounce.
 * @param waitMs - Delay in milliseconds.
 * @returns Debounced function with `cancel`.
 */
export function debounce<TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  waitMs: number
): ((...args: TArgs) => void) & { cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const debounced = ((...args: TArgs) => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      timeoutId = undefined;
      fn(...args);
    }, waitMs);
  }) as ((...args: TArgs) => void) & { cancel: () => void };

  debounced.cancel = () => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
      timeoutId = undefined;
    }
  };

  return debounced;
}

/**
 * Returns a throttled function that invokes `fn` at most once per `waitMs`
 * milliseconds.
 *
 * @param fn - Function to throttle.
 * @param waitMs - Minimum interval between invocations in milliseconds.
 * @returns Throttled function.
 */
export function throttle<TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  waitMs: number
): (...args: TArgs) => void {
  let lastCall = 0;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let lastArgs: TArgs | undefined;

  return (...args: TArgs) => {
    const now = Date.now();
    const remaining = waitMs - (now - lastCall);
    lastArgs = args;

    if (remaining <= 0) {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
      lastCall = now;
      fn(...args);
      return;
    }

    if (timeoutId === undefined) {
      timeoutId = setTimeout(() => {
        lastCall = Date.now();
        timeoutId = undefined;
        if (lastArgs) {
          fn(...lastArgs);
        }
      }, remaining);
    }
  };
}
